import { useEffect, useMemo, useState } from 'react';

import { Alert, Button, Checkbox, Group, MultiSelect, Select, Stack, Text } from '@mantine/core';

import {
  NotificationAddress,
  NotificationRule,
  NotificationRuleInput,
  RECIPIENT_MODE_OPTIONS,
  RecipientMode,
  TeamMember,
} from '@/types/team-settings';

interface NotificationRuleEditorProps {
  /** Distinguishes this editor's controls from its siblings'. */
  idPrefix: string;
  /** This rule as stored, or null when it is inheriting. */
  rule: NotificationRule | null;
  /** What to start from when there is no rule of its own. */
  seed: Omit<NotificationRule, 'eventType'>;
  members: TeamMember[];
  /** The roster could not be read, so people cannot be chosen right now. */
  membersUnavailable: boolean;
  addresses: NotificationAddress[];
  saveLabel: string;
  /** Present only when there is something to remove; explains what removing does. */
  removeLabel?: string;
  onSave: (input: NotificationRuleInput) => Promise<void>;
  onRemove?: () => Promise<void>;
}

const ADDRESS_STATUS_SUFFIX: Record<NotificationAddress['status'], string> = {
  verified: '',
  pending: ' — awaiting confirmation',
  expired: ' — confirmation link expired',
};

/**
 * Who one notification (or the team default) goes to: a base audience, the
 * people (and optionally the creator) if it is `selected`, and an optional copy
 * address — or `off`, which sends nothing and so offers neither.
 *
 * Holds its own draft and only reports a change on Save. It never presents
 * failures itself — `onSave` / `onRemove` are expected to, and to resolve
 * either way — so that the draft is kept when a save is refused and can be
 * corrected rather than retyped.
 */
export function NotificationRuleEditor({
  idPrefix,
  rule,
  seed,
  members,
  membersUnavailable,
  addresses,
  saveLabel,
  removeLabel,
  onSave,
  onRemove,
}: NotificationRuleEditorProps) {
  const start = rule ?? { ...seed, eventType: '' };

  // People who have left the team can't be notified, and the API rejects them.
  // Only judged once the roster has actually loaded.
  const knownIds = useMemo(() => new Set(members.map((m) => m.id)), [members]);
  const canJudgeRoster = !membersUnavailable;
  const startingIds = canJudgeRoster
    ? start.selectedUserIds.filter((id) => knownIds.has(id))
    : start.selectedUserIds;
  const departed = start.selectedUserIds.length - startingIds.length;

  const [mode, setMode] = useState<RecipientMode>(start.baseMode);
  const [selected, setSelected] = useState<string[]>(startingIds.map(String));
  const [includeCreator, setIncludeCreator] = useState(start.includeCreator);
  const [copyAddress, setCopyAddress] = useState<string | null>(start.copyAddress);
  const [busy, setBusy] = useState(false);

  // Adopt the stored rule when it changes underneath us (a save elsewhere, a
  // reset) — or, with no rule of its own, the default it starts from. Keyed on
  // content so typing is never overwritten.
  const storedKey = JSON.stringify(rule);
  const seedKey = JSON.stringify(seed);
  useEffect(() => {
    const next = rule ?? { ...seed, eventType: '' };
    setMode(next.baseMode);
    setSelected(
      (canJudgeRoster
        ? next.selectedUserIds.filter((id) => knownIds.has(id))
        : next.selectedUserIds
      ).map(String)
    );
    setIncludeCreator(next.includeCreator);
    setCopyAddress(next.copyAddress);
  }, [storedKey, seedKey, canJudgeRoster, knownIds]);

  const selectedIds = selected.map(Number);
  const dirty =
    rule === null ||
    mode !== rule.baseMode ||
    (mode === 'off' ? null : copyAddress) !== rule.copyAddress ||
    (mode === 'selected' &&
      (includeCreator !== rule.includeCreator ||
        JSON.stringify([...selectedIds].sort()) !==
          JSON.stringify([...rule.selectedUserIds].sort())));

  const memberOptions = members.map((m) => ({
    value: String(m.id),
    label: m.email ? `${m.name} (${m.email})` : m.name,
  }));
  const quiet = members.filter((m) => m.isMutedTeamProjects && selectedIds.includes(m.id));

  const addressOptions = addresses.map((a) => ({
    value: a.email,
    label: `${a.email}${ADDRESS_STATUS_SUFFIX[a.status]}`,
  }));
  const chosenAddress = addresses.find((a) => a.email === copyAddress);
  const modeDescription = RECIPIENT_MODE_OPTIONS.find((o) => o.value === mode)?.description;

  const save = async () => {
    setBusy(true);
    try {
      await onSave({
        baseMode: mode,
        selectedUserIds: mode === 'selected' ? selectedIds : [],
        includeCreator: mode === 'selected' && includeCreator,
        copyAddress: mode === 'off' ? null : copyAddress,
      });
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!onRemove) {
      return;
    }
    setBusy(true);
    try {
      await onRemove();
    } finally {
      setBusy(false);
    }
  };

  return (
    <Stack gap="sm" data-testid={`rule-editor-${idPrefix}`}>
      <Select
        label="Who is told"
        aria-label={`Who is told — ${idPrefix}`}
        data={RECIPIENT_MODE_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
        value={mode}
        onChange={(value) => value && setMode(value as RecipientMode)}
        allowDeselect={false}
        disabled={busy}
        description={modeDescription}
      />

      {mode === 'selected' && (
        <Stack gap={4}>
          {membersUnavailable ? (
            <Alert color="orange" variant="light">
              The team&apos;s people couldn&apos;t be loaded, so they can&apos;t be chosen right
              now. Try again shortly.
            </Alert>
          ) : (
            <MultiSelect
              label="People"
              aria-label={`People — ${idPrefix}`}
              data={memberOptions}
              value={selected}
              onChange={setSelected}
              searchable
              clearable
              disabled={busy}
              placeholder={selected.length === 0 ? 'Choose people' : undefined}
            />
          )}
          {departed > 0 && (
            <Text size="xs" c="dimmed">
              {departed === 1
                ? '1 person who has left the team was removed from this list.'
                : `${departed} people who have left the team were removed from this list.`}
            </Text>
          )}
          <Checkbox
            label="Also send to the person who raised the report"
            aria-label={`Include the creator — ${idPrefix}`}
            checked={includeCreator}
            onChange={(event) => setIncludeCreator(event.currentTarget.checked)}
            disabled={busy}
            mt={4}
          />
          {!membersUnavailable && selected.length === 0 && !includeCreator && (
            <Text size="xs" c="dimmed">
              Nobody chosen — the report&apos;s creator will be told instead.
            </Text>
          )}
          {quiet.length > 0 && (
            <Text size="xs" c="orange.8" data-testid={`quiet-warning-${idPrefix}`}>
              {quiet.map((m) => m.name).join(', ')} {quiet.length === 1 ? 'has' : 'have'} chosen to
              hear only about reports they created themselves, so choosing{' '}
              {quiet.length === 1 ? 'them' : 'these people'} won&apos;t override that.
            </Text>
          )}
        </Stack>
      )}

      {mode !== 'off' && (
        <Stack gap={4}>
          <Select
            label="Also copy to"
            aria-label={`Also copy to — ${idPrefix}`}
            data={addressOptions}
            value={copyAddress}
            onChange={setCopyAddress}
            clearable
            disabled={busy}
            placeholder={addresses.length === 0 ? 'Add a copy address above first' : 'No copy'}
          />
          {chosenAddress && chosenAddress.status !== 'verified' && (
            <Text size="xs" c="orange.8">
              Nothing is sent to this address until its owner confirms it.
            </Text>
          )}
        </Stack>
      )}

      <Group justify="space-between">
        <Button
          size="xs"
          onClick={() => void save()}
          loading={busy}
          disabled={!dirty}
          data-testid={`rule-save-${idPrefix}`}
        >
          {saveLabel}
        </Button>
        {removeLabel && rule && onRemove && (
          <Button
            size="xs"
            variant="subtle"
            color="gray"
            onClick={() => void remove()}
            disabled={busy}
            data-testid={`rule-remove-${idPrefix}`}
          >
            {removeLabel}
          </Button>
        )}
      </Group>
    </Stack>
  );
}
